import React, { useState, useEffect } from 'react';

const ServerList = ({ sortable = false, mod = 'war3ft' }) => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const fetchServers = async () => {
      try {
        // wc3mods.net's all-mods list passes mod: null; don't send it as "null"
        const response = await fetch(mod ? `/api/servers?mod=${encodeURIComponent(mod)}` : '/api/servers');
        if (!response.ok) {
          throw new Error('Failed to fetch servers');
        }
        const data = await response.json();
        setServers(data.servers || []); // Access the servers array from the response
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchServers();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchServers, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSort = (field) => {
    if (!sortable) return;
    
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIndicator = ({ field }) => {
    if (!sortable || sortField !== field) return null;
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const getDisplayServers = () => {
    if (!sortable || !sortField) return servers;
    
    return [...servers].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested fields for version
      if (sortField === 'war3_version') {
        aValue = a.war3_version;
        bValue = b.war3_version;
      } else if (sortField === 'players') {
        // Convert players string to number for proper sorting
        aValue = parseInt(a.players);
        bValue = parseInt(b.players);
      }

      if (aValue === bValue) return 0;
      
      const comparison = aValue > bValue ? 1 : -1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Add copy function
  const handleCopy = (text, serverId) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(serverId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  if (servers.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No active servers available</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-800">
            <th 
              className={`text-left py-3 px-4 ${sortable ? 'cursor-pointer hover:bg-[#1a1f2e]' : ''}`}
              onClick={() => handleSort('server_name')}
            >
              Server Name
              <SortIndicator field="server_name" />
            </th>
            <th 
              className={`text-left py-3 px-4 ${sortable ? 'cursor-pointer hover:bg-[#1a1f2e]' : ''}`}
              onClick={() => handleSort('app_id')}
            >
              Game
              <SortIndicator field="app_id" />
            </th>
            <th 
              className={`text-left py-3 px-4 ${sortable ? 'cursor-pointer hover:bg-[#1a1f2e]' : ''}`}
              onClick={() => handleSort('map')}
            >
              Map
              <SortIndicator field="map" />
            </th>
            <th 
              className={`text-left py-3 px-4 ${sortable ? 'cursor-pointer hover:bg-[#1a1f2e]' : ''}`}
              onClick={() => handleSort('players')}
            >
              Players
              <SortIndicator field="players" />
            </th>
            <th 
              className={`text-left py-3 px-4 ${sortable ? 'cursor-pointer hover:bg-[#1a1f2e]' : ''}`}
              onClick={() => handleSort('war3_version')}
            >
              Version
              <SortIndicator field="war3_version" />
            </th>
            <th 
              className={`text-left py-3 px-4 ${sortable ? 'cursor-pointer hover:bg-[#1a1f2e]' : ''}`}
              onClick={() => handleSort('country_code')}
            >
              Country
              <SortIndicator field="country_code" />
            </th>
            <th 
              className={`text-left py-3 px-4 ${sortable ? 'cursor-pointer hover:bg-[#1a1f2e]' : ''}`}
              onClick={() => handleSort('status')}
            >
              Status
              <SortIndicator field="status" />
            </th>
            <th className="text-left py-3 px-4">Action</th>
          </tr>
        </thead>
        <tbody>
          {getDisplayServers().map((server, index) => (
            <tr key={index} className="border-b border-gray-800 hover:bg-[#1a1f2e]">
              <td className="py-3 px-4">
                <div className="flex items-center">
                  <div className="w-5 h-5 flex items-center justify-center text-secondary mr-2">
                    <i className="ri-server-line"></i>
                  </div>
                  <div className="flex flex-col">
                    <span>{server.server_name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{server.ip}:{server.port}</span>
                      <button
                        onClick={() => handleCopy(`${server.ip}:${server.port}`, index)}
                        className="text-gray-500 hover:text-gray-300 transition-colors"
                        title="Copy server address"
                      >
                        {copiedId === index ? (
                          <i className="ri-check-line text-xs text-green-500"></i>
                        ) : (
                          <i className="ri-file-copy-line text-xs"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4">
                <img 
                  src={`/app_images/app_id_${server.app_id}.png`} 
                  alt="Game Icon" 
                  className="w-8 h-8"
                  title={server.app_id === 10 ? "Counter-Strike 1.6" : server.app_id === 80 ? "Counter-Strike: Condition Zero" : "Day of Defeat"}
                />
              </td>
              <td className="py-3 px-4">{server.map}</td>
              <td className="py-3 px-4">{server.players}</td>
              <td className="py-3 px-4">
                <div className="flex flex-col">
                  <span className="text-sm">{server.war3_version}</span>
                </div>
              </td>
              <td className="py-3 px-4">{server.country_code || 'N/A'}</td>
              <td className="py-3 px-4">
                <span className={`server-status ${server.status.toLowerCase() !== 'online' ? 'offline' : ''}`}>
                  {server.status}
                </span>
              </td>
              <td className="py-3 px-4">
                <button 
                  onClick={() => window.location.href = server.connect_url}
                  className="bg-primary text-white py-1 px-4 rounded-button text-sm custom-button whitespace-nowrap"
                >
                  Connect
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-500 mt-4 text-right">
        Server data from <a href="https://lobbyverse.com" className="text-primary hover:underline">LobbyVerse</a>, our
        server browser that checks every server's players are real. See the <a href="https://lobbyverse.com/communities/warcraft-3-official" className="text-primary hover:underline">official servers there</a>.
      </p>
    </div>
  );
};

export { ServerList };
export default ServerList; 